import { decorate, injectable, unmanaged } from 'inversify'
import { pickBy } from 'lodash'
import { makeObservable, observable, action, computed, runInAction } from 'mobx'
import debugModule from 'debug'

const debugLog = debugModule('app:Store 🏪')
debugLog.enabled = false


export type Extra<T> = T & {
    /** when it was first put into the store */
    localStoreDate?: string
}

class Store<T extends { id?: number }> {
    /** Registry of every live Store instance, used for cascading clears */
    private static instances = new Set<Store<any>>()

    /** Observable Map of DataType objects*/
    objectsMap: Map<number, Extra<T>> = new Map([])

    /** Computed array from objectMap.values() */
    get objectsArray() {
        return Array.from(this.objectsMap.values())
    }

    constructor(readonly fields: string[]) {
        makeObservable(this, {
            objectsMap: observable,
            update: action,
            deleteById: action,
            deleteByIds: action,
            objectsArray: computed,
            clear: action
        })

        Store.instances.add(this)
    }

    clear() {
        this.objectsMap.clear()
    }

    /**
     * Clears every live Store instance in a single MobX action batch.
     * Lets callers wipe all in-memory store data without importing each store.
     */
    static clearAll() {
        runInAction(() => {
            for (const instance of Store.instances) {
                instance.clear()
            }
        })
    }

    /**
     * Total number of objects held across every live Store instance.
     * Reactive: reads each store's observable map, so observers re-render
     * as stores fill or are cleared.
     */
    static get totalCount() {
        let count = 0
        for (const instance of Store.instances) {
            count += instance.objectsMap.size
        }
        return count
    }

    /**
     * Removes this instance from the registry to prevent leaks.
     * DI stores are app-lifetime singletons so this is currently unused at
     * runtime, but it is the correct teardown primitive for short-lived stores.
     */
    dispose() {
        Store.instances.delete(this)
    }

    /**
     * Empties the instance registry. Exposed for test isolation so a global
     * registry does not accumulate instances across cases.
     */
    static resetRegistry() {
        Store.instances.clear()
    }

    /**
     * Merges in or inserts data received from Api calls
     * @param data can be an object or an array of objects
     * @returns
     */
    update(data: T | T[] | undefined) {
        if (data === undefined) return

        const startTime = Date.now()

        // make data always an array
        const arr = Array.isArray(data) ? data : [data]

        // iterate over data
        for (const i of arr) {
            if (!i?.id || isNaN(Number(i.id))) continue

            const id = Number(i.id)

            // Pick fields using lodash.pickBy<>() that are listed in 'this.fields' and whose value is not undefined, we don't want to merge in undefined values
            const pickedData = pickBy<Extra<T>>(i, (v, k)=> this.fields.includes(k) && v !== undefined) as Extra<T>

            pickedData.localStoreDate = String(new Date())

            // if we have this object in objectsMap, merge in new data
            if (this.objectsMap.has(id)){

                // merge picked fields in to existing object
                // should keep the object observable fields
                Object.assign(this.objectsMap.get(id), pickedData)
            } else {

                // insert new object
                this.objectsMap.set(id, pickedData)
            }
        }

        this.debugPrintPerformance(`update() ${arr.length} items`, startTime)
    }

    /**
     * Find one Object by Id
     * @param id Id of DataType object
     * @returns DataType or undefined
     */
    findById(id: number): Extra<T> | undefined{
        id = Number(id)
        // can not do anything with a NaN id
        if (isNaN(id)) return undefined
        return this.objectsMap.get(id)
    }

    /**
     * Find array of objects by ID
     * @param ids Array of Ids
     * @returns Array of found DataType objects
     */
    findByIds(ids: (number | undefined)[]): Extra<T>[] {
        return ids
            .map(id => Number(id))
            .filter(id => !isNaN(id))
            .map(id => this.objectsMap.get(id))
            .filter((i): i is Extra<T> => Boolean(i))
    }

    /**
     * Find one object based on predicate function
     * @param predicate Function (o:DataType) => boolean
     * @returns
     */
    findOne(predicate: (o:Extra<T>) => boolean):Extra<T> | undefined {
        for (const v of this.objectsMap.values()) {
            if (predicate(v)) return v
        }
        return undefined
    }

    /**
     * Find array of objects based on predicate function
     * @param predicate
     * @returns
     */
    find(predicate: (o:Extra<T>) => boolean):Extra<T>[] {
        const result: Extra<T>[] = []
        for (const v of this.objectsMap.values()) {
            if (predicate(v)) result.push(v)
        }
        return result
    }

    /**
     * Remove an item from the store
     */
    deleteById(id: number): boolean{
        return this.objectsMap.delete(id)
    }

    /**
     * Remove multiple items from the store
     */
    deleteByIds(ids: number[]) {
        for (const id of ids) {
            this.objectsMap.delete(id)
        }
    }

    debugPrintPerformance(operation: string, startTime: number) {
        const duration = Date.now() - startTime
        debugLog(`${this.constructor.name} ${operation} completed in [${duration}ms].`)
    }

}

decorate(injectable(), Store)
decorate(unmanaged(), Store, 0 ) // Tells inversify to ignore Store.constructor() parameter 0 since we use it as a constant value for configuration

export { Store }
